import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  isSatisfied,
  placeCard,
  stepNote,
  type OnboardingEvent,
  type TourDefinition,
  type TourStep,
} from '@/src/lib/onboarding/tour-machine'

const PHONE_WIDTH = 640
const PADDING = 4
const now = () => new Date().toISOString()

function isVisible(element: Element) {
  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return false
  // The desktop sidebar slides off the left edge when it is collapsed.
  if (rect.right <= 0 || rect.left >= window.innerWidth) return false
  return getComputedStyle(element).visibility !== 'hidden'
}

/** The first element on screen marked with this tour name. */
export function findTarget(name: string) {
  for (const element of document.querySelectorAll<HTMLElement>(`[data-tour~="${name}"]`)) {
    if (isVisible(element)) return element
  }
  return null
}

function resolve(step: TourStep) {
  const primary = step.target ? findTarget(step.target) : null
  if (primary) return { element: primary, found: 'found' as const }
  const fallback = step.fallback ? findTarget(step.fallback.target) : null
  if (fallback) return { element: fallback, found: 'fallback' as const }
  return { element: null, found: 'none' as const }
}

/** A dialog, sheet or popover other than this card is open. */
function otherLayers(card: HTMLElement | null) {
  return [
    ...document.querySelectorAll<HTMLElement>(
      '[role="dialog"][data-state], [role="alertdialog"][data-state], [data-radix-popper-content-wrapper]',
    ),
  ].filter((layer) => !card?.contains(layer))
}

/** Layers that come before the tour: anything but the phone menu, which the tour opens itself. */
const blocking = (layers: HTMLElement[]) =>
  layers.filter((layer) => !layer.closest('[data-mobile="true"]'))

const overlaps = (a: DOMRect, b: DOMRect) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom

/**
 * A dialog or drawer that opens while the card is up hides everything else
 * from screen readers: the card itself, or, since it keeps live regions, the
 * card's buttons. The card is about what is in the dialog, so keep it readable.
 */
function unhide(card: HTMLElement) {
  if (card.hasAttribute('aria-hidden')) card.removeAttribute('aria-hidden')
  for (const hidden of card.querySelectorAll('[data-aria-hidden]')) {
    hidden.removeAttribute('aria-hidden')
    hidden.removeAttribute('data-aria-hidden')
  }
}

function bringIntoView(element: HTMLElement, phone: boolean) {
  const rect = element.getBoundingClientRect()
  if (rect.top >= 8 && rect.bottom <= window.innerHeight - 8) return
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  element.scrollIntoView({
    block: phone ? 'start' : 'center',
    inline: 'nearest',
    behavior: reduced ? 'auto' : 'smooth',
  })
}

/**
 * The running tour: a highlight around the step's element and a card beside
 * it. Neither blocks the page, so people can do what the step asks. The card
 * sits above dialogs and drawers (so a step inside one still has its card) and
 * steps aside while a menu or popover the person opened overlaps it.
 */
export function TourLayer({
  tour,
  index,
  dispatch,
  focusRequest,
}: {
  tour: TourDefinition
  index: number
  dispatch: (event: OnboardingEvent) => void
  /** Changes when a tour is started from Getting started: the card takes focus. */
  focusRequest: number
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const titleId = useId()
  const bodyId = useId()
  const cardRef = useRef<HTMLDivElement>(null)
  const spotlightRef = useRef<HTMLDivElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)
  const handledFocus = useRef(0)
  const [found, setFound] = useState<'found' | 'fallback' | 'none'>('none')

  const step = tour.steps[index]
  const last = index === tour.steps.length - 1

  // Each frame while the tour runs: find the element, keep the highlight and
  // card on it, and notice when the person has done what the step asks.
  useEffect(() => {
    let frame = 0
    let observed: boolean | null = null
    let scrolledFor: HTMLElement | null = null
    let reported: string | null = null

    const tick = () => {
      const card = cardRef.current
      const spotlight = spotlightRef.current
      const phone = window.innerWidth < PHONE_WIDTH
      const { element, found: resolved } = resolve(step)

      if (resolved !== reported) {
        reported = resolved
        setFound(resolved)
      }
      if (element && element !== scrolledFor) {
        scrolledFor = element
        bringIntoView(element, phone)
      }

      const rect = element?.getBoundingClientRect() ?? null
      const layers = otherLayers(card)
      if (spotlight) {
        if (rect) {
          spotlight.style.display = 'block'
          spotlight.style.top = `${rect.top - PADDING}px`
          spotlight.style.left = `${rect.left - PADDING}px`
          spotlight.style.width = `${rect.width + PADDING * 2}px`
          spotlight.style.height = `${rect.height + PADDING * 2}px`
          // A menu opened from the highlighted field shows above the shade.
          const popper = layers.some((layer) =>
            layer.hasAttribute('data-radix-popper-content-wrapper'),
          )
          spotlight.style.zIndex = popper ? '40' : '60'
        } else {
          spotlight.style.display = 'none'
        }
      }
      if (card) {
        const box = rect && {
          top: rect.top - PADDING,
          left: rect.left - PADDING,
          width: rect.width + PADDING * 2,
          height: rect.height + PADDING * 2,
        }
        // Stay off the field the person is in, e.g. the one a form sent them back to.
        const focused = document.activeElement
        const avoid =
          focused instanceof HTMLElement && focused !== document.body && !card.contains(focused)
            ? [focused.getBoundingClientRect()]
            : []
        const place = placeCard(
          box,
          { width: card.offsetWidth, height: card.offsetHeight },
          { width: window.innerWidth, height: window.innerHeight },
          { prefer: step.side, phone, avoid },
        )
        card.style.top = `${place.top}px`
        card.style.left = `${place.left}px`
        const cardRect = card.getBoundingClientRect()
        const covered = layers.some(
          (layer) =>
            layer.hasAttribute('data-radix-popper-content-wrapper') &&
            overlaps(layer.getBoundingClientRect(), cardRect),
        )
        card.style.visibility = covered ? 'hidden' : 'visible'
        unhide(card)

        // Started from Getting started: take focus once its dialog has gone.
        if (handledFocus.current !== focusRequest && !blocking(layers).length) {
          handledFocus.current = focusRequest
          nextRef.current?.focus({ preventScroll: true })
        }
      }

      if (step.advance) {
        const satisfied = isSatisfied(step.advance, {
          pathname: window.location.pathname,
          present: (name) => findTarget(name) !== null,
        })
        if (satisfied !== observed) {
          observed = satisfied
          dispatch({ type: 'OBSERVE', stepId: step.id, satisfied, at: now() })
        }
      }

      frame = window.requestAnimationFrame(tick)
    }

    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [step, dispatch, focusRequest])

  // Esc ends the tour. While another dialog or menu is open, Esc closes that
  // first, unless the focus is on this card.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const card = cardRef.current
      const onCard = Boolean(card && event.target instanceof Node && card.contains(event.target))
      // A non-modal panel, such as Beacon's, closes on Esc pressed inside it.
      const panel =
        event.target instanceof Element
          ? event.target.closest('[role="dialog"]:not([data-tour-card])')
          : null
      const inPanel = Boolean(panel && blocking([panel as HTMLElement]).length)
      if (!onCard && (inPanel || blocking(otherLayers(card)).length > 0)) return
      if (onCard) event.stopPropagation()
      dispatch({ type: 'SKIP', at: now() })
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [dispatch])

  // A dialog or drawer behind the card would take a press on the card as a
  // click outside it, and close; keep the card's presses and focus its own.
  useEffect(() => {
    const card = cardRef.current
    if (!card) return
    const keep = (event: Event) => event.stopPropagation()
    card.addEventListener('pointerdown', keep)
    card.addEventListener('focusin', keep)
    return () => {
      card.removeEventListener('pointerdown', keep)
      card.removeEventListener('focusin', keep)
    }
  }, [])

  function next() {
    if (step.advance && step.perform) {
      const done = isSatisfied(step.advance, {
        pathname: window.location.pathname,
        present: (name) => findTarget(name) !== null,
      })
      if (!done) {
        // Do it for them; the step moves on when it has happened. A form that
        // isn't ready shows why, and the tour waits.
        if ('navigate' in step.perform) {
          navigate(step.perform.navigate)
          return
        }
        const control = findTarget(step.perform.click)
        if (control) {
          control.click()
          return
        }
      }
    }
    dispatch({ type: 'NEXT', at: now() })
  }

  const note = stepNote(step, { pathname, target: found })
  const lone = !step.target

  return createPortal(
    <>
      {lone && <div aria-hidden className="pointer-events-none fixed inset-0 z-[60] bg-black/45" />}
      <div
        ref={spotlightRef}
        aria-hidden
        data-tour-spotlight=""
        className="pointer-events-none fixed z-[60] hidden rounded-[10px]"
        style={{
          boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.45)',
          outline: '2px solid rgb(255 255 255 / 0.9)',
          outlineOffset: 2,
        }}
      />
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        data-tour-card=""
        // Hidden until the first frame has put it in place.
        className="pointer-events-auto invisible fixed top-4 left-4 z-[60] w-[min(22rem,calc(100vw-2rem))] rounded-2xl border bg-card p-4 text-card-foreground shadow-lg"
      >
        <div aria-live="polite">
          <p className="text-xs text-muted-foreground">
            {tour.title} · Step {index + 1} of {tour.steps.length}
          </p>
          <h2 id={titleId} className="mt-1 text-base font-semibold">
            {step.title}
          </h2>
          <p id={bodyId} className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {step.body}
          </p>
        </div>
        {note && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
            <MapPin className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1">
              {note.kind === 'elsewhere' ? `This step is on ${note.label}.` : note.text}
            </span>
            {note.kind === 'elsewhere' && note.href && (
              <Button size="xs" variant="outline" onClick={() => navigate(note.href!)}>
                Go there
              </Button>
            )}
          </div>
        )}
        <div className="mt-3 flex gap-1" aria-hidden>
          {tour.steps.map((candidate, position) => (
            <span
              key={candidate.id}
              className={cn('h-1 flex-1 rounded-full bg-muted', position <= index && 'bg-primary')}
            />
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <Button size="sm" variant="ghost" onClick={() => dispatch({ type: 'SKIP', at: now() })}>
            Skip tour
          </Button>
          <div className="flex gap-2">
            {index > 0 && (
              <Button size="sm" variant="outline" onClick={() => dispatch({ type: 'BACK' })}>
                <ArrowLeft />
                Back
              </Button>
            )}
            <Button ref={nextRef} size="sm" onClick={next}>
              {last ? (
                <>
                  <Check />
                  Done
                </>
              ) : (
                <>
                  Next
                  <ArrowRight />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
